/**
 * Секція «Користувачі і доступи» у AdminPage (Порція Виробництво.5 UX).
 *
 * Активні / Запрошення в очікуванні / Призупинені.
 *
 * ч.1073 (рішення власника):
 *  - новий користувач зʼявляється ЛИШЕ через запрошення — ручне створення з
 *    паролем прибрано: запрошення саме заводить обліковий запис, якщо його
 *    немає, а пароль людина задає сама (адміністратор паролів не знає);
 *  - запрошення надсилається листом (і в Telegram існуючому користувачу),
 *    «надіслати ще раз» — повторний лист із новим посиланням;
 *  - доступи «Користувача» до організацій — кнопка «доступи»
 *    (MemberAccessDialog): додати/прибрати організацію чи змінити роль у
 *    будь-який момент, і до прийняття запрошення теж.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listMemberships,
  listInvitations,
  suspendMembership,
  resumeMembership,
  deleteMembership,
  revokeInvitation,
  createInvitation,
  type InvitationCreated,
  type InvitationListItem,
  type MembershipListItem,
  type TenantRole,
} from '@/api/invitations';
import { useConfirm } from '@/components/ConfirmProvider';
import { useToast } from '@/components/ToastProvider';
import RequirePermission from '@/auth/RequirePermission';
import { extractApiError } from '@/utils/extractApiError';
import InviteUserDialog from '../team/InviteUserDialog';
import InvitationResult from '../team/InvitationResult';
import MemberAccessDialog from '../team/MemberAccessDialog';
import { getMe } from '@/api/auth';
import { tenantRoleLabel } from '@/auth/roleLabels';

interface AccessTarget {
  membershipId: string;
  email: string;
  pending: boolean;
}

/** «3 організації» / «1 організація» / «5 організацій». */
function orgsCountLabel(n: number): string {
  const d = n % 10;
  const dd = n % 100;
  const word = d === 1 && dd !== 11
    ? 'організація'
    : d >= 2 && d <= 4 && (dd < 12 || dd > 14) ? 'організації' : 'організацій';
  return `${n} ${word}`;
}

/** Колонка «Доступ»: власник/адміністратор — усі; користувач — скільки. */
function AccessCell({ role, count }: { role: TenantRole; count: number }) {
  if (role !== 'MEMBER') return <span>усі організації</span>;
  if (count === 0) {
    return (
      <span
        style={{ color: 'var(--badge-amber-fg)' }}
        title="Без доступу до жодної організації користувач нічого не бачить"
      >
        немає доступу
      </span>
    );
  }
  return <span>{orgsCountLabel(count)}</span>;
}

export default function TeamSection() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const [accessTarget, setAccessTarget] = useState<AccessTarget | null>(null);
  const [resent, setResent] = useState<InvitationCreated | null>(null);
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const toast = useToast();

  // ч.1423: людей з акаунта прибирає лише власник (сервер відповість 403 решті).
  const me = useQuery({ queryKey: ['me'], queryFn: getMe });
  const deleteAction = (m: MembershipListItem): RowAction[] => (me.data?.tenant_role === 'OWNER'
    ? [{ label: 'видалити', onClick: () => onDeleteMembership(m), variant: 'danger' }] : []);

  const active = useQuery({
    queryKey: ['memberships', 'ACTIVE'],
    queryFn: () => listMemberships('ACTIVE'),
  });
  const suspended = useQuery({
    queryKey: ['memberships', 'SUSPENDED'],
    queryFn: () => listMemberships('SUSPENDED'),
  });
  const invited = useQuery({
    queryKey: ['invitations'],
    queryFn: listInvitations,
  });

  function refreshAll() {
    queryClient.invalidateQueries({ queryKey: ['memberships'] });
    queryClient.invalidateQueries({ queryKey: ['invitations'] });
  }

  function fail(fallback: string) {
    return (e: unknown) =>
      toast.show({ type: 'error', message: extractApiError(e, fallback) });
  }

  const suspendMut = useMutation({
    mutationFn: suspendMembership,
    onSuccess: () => refreshAll(),
    onError: fail('Не вдалося призупинити доступ'),
  });
  const resumeMut = useMutation({
    mutationFn: resumeMembership,
    onSuccess: () => refreshAll(),
    onError: fail('Не вдалося відновити доступ'),
  });
  const deleteMut = useMutation({
    mutationFn: deleteMembership,
    onSuccess: () => refreshAll(),
    onError: fail('Не вдалося видалити'),
  });
  const revokeMut = useMutation({
    mutationFn: revokeInvitation,
    onSuccess: () => refreshAll(),
    onError: fail('Не вдалося скасувати запрошення'),
  });
  // «Надіслати ще раз» — те саме запрошення (email + роль) без доступів:
  // backend лишає раніше вибрані доступи і видає нове посилання.
  const resendMut = useMutation({
    mutationFn: (inv: InvitationListItem) =>
      createInvitation({ email: inv.email, tenant_role: inv.tenant_role }),
    onSuccess: (data) => {
      setResent(data);
      refreshAll();
    },
    onError: fail('Не вдалося надіслати запрошення'),
  });

  async function onSuspend(m: MembershipListItem) {
    const ok = await confirm({
      message: `Призупинити доступ ${m.email}? Користувач не зможе входити в акаунт, поки доступ не буде відновлено.`,
      okLabel: 'призупинити',
      cancelLabel: 'скасувати',
    });
    if (ok) suspendMut.mutate(m.id);
  }

  async function onDeleteMembership(m: MembershipListItem) {
    const ok = await confirm({
      message: `Видалити доступ ${m.email}? Цю дію неможливо скасувати. Користувач втратить доступ повністю.`,
      okLabel: 'видалити',
      cancelLabel: 'скасувати',
    });
    if (ok) deleteMut.mutate(m.id);
  }

  async function onRevoke(inv: InvitationListItem) {
    const ok = await confirm({
      message: `Скасувати запрошення для ${inv.email}? Посилання стане недійсним.`,
      okLabel: 'скасувати запрошення',
      cancelLabel: 'залишити',
    });
    if (ok) revokeMut.mutate(inv.id);
  }

  function editAccess(membershipId: string, email: string, pending: boolean) {
    setAccessTarget({ membershipId, email, pending });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginBottom: '1rem' }}>
        <RequirePermission code="admin.users.manage">
          <button
            type="button"
            className="primary"
            onClick={() => setInviteOpen(true)}
          >
            запросити користувача
          </button>
        </RequirePermission>
      </div>

      <Section title={`Активні (${active.data?.length ?? 0})`}>
        <MembershipsTable
          loading={active.isLoading}
          items={active.data ?? []}
          actions={(m) => [
            ...(m.tenant_role === 'MEMBER'
              ? [{ label: 'доступи', onClick: () => editAccess(m.id, m.email, false), variant: 'default' as const }]
              : []),
            { label: 'призупинити', onClick: () => onSuspend(m), variant: 'default' },
            ...deleteAction(m),
          ]}
        />
      </Section>

      <Section title={`Запрошення в очікуванні (${invited.data?.length ?? 0})`}>
        <InvitationsTable
          loading={invited.isLoading}
          items={invited.data ?? []}
          actions={(inv) => [
            ...(inv.tenant_role === 'MEMBER'
              ? [{ label: 'доступи', onClick: () => editAccess(inv.id, inv.email, true), variant: 'default' as const }]
              : []),
            {
              label: 'надіслати ще раз',
              onClick: () => resendMut.mutate(inv),
              variant: 'default',
              disabled: resendMut.isPending,
            },
            { label: 'скасувати', onClick: () => onRevoke(inv), variant: 'danger' },
          ]}
        />
      </Section>

      <Section title={`Призупинені (${suspended.data?.length ?? 0})`} collapsedByDefault>
        <MembershipsTable
          loading={suspended.isLoading}
          items={suspended.data ?? []}
          actions={(m) => [
            { label: 'відновити', onClick: () => resumeMut.mutate(m.id), variant: 'default' },
            ...deleteAction(m),
          ]}
        />
      </Section>

      {inviteOpen && (
        <InviteUserDialog
          onClose={() => setInviteOpen(false)}
          onCreated={() => {
            setInviteOpen(false);
            refreshAll();
          }}
        />
      )}

      {accessTarget && (
        <MemberAccessDialog
          membershipId={accessTarget.membershipId}
          email={accessTarget.email}
          pending={accessTarget.pending}
          onClose={() => setAccessTarget(null)}
        />
      )}

      {resent && (
        <>
          <div className="backdrop" onClick={() => setResent(null)} />
          <div className="drawer" style={{ width: 'min(560px, 100vw)' }}>
            <h2>Запрошення надіслано ще раз</h2>
            <InvitationResult created={resent} />
            <div className="drawer__actions">
              <button type="button" className="primary" onClick={() => setResent(null)}>
                готово
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// =============================================================================
// Section (collapsible)
// =============================================================================

function Section({
  title, children, collapsedByDefault = false,
}: {
  title: string;
  children: React.ReactNode;
  collapsedByDefault?: boolean;
}) {
  const [open, setOpen] = useState(!collapsedByDefault);
  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3 style={{ margin: 0 }}>{title}</h3>
        <span style={{ color: 'var(--text-secondary)' }}>{open ? '▾' : '▸'}</span>
      </div>
      {open && <div style={{ marginTop: '1rem' }}>{children}</div>}
    </div>
  );
}

// =============================================================================
// Tables
// =============================================================================

interface RowAction {
  label: string;
  onClick: () => void;
  variant: 'default' | 'danger';
  disabled?: boolean;
}

function ActionButtons({ actions }: { actions: RowAction[] }) {
  return (
    <>
      {actions.map((a) => (
        <button
          key={a.label}
          type="button"
          className={a.variant === 'danger' ? 'danger' : ''}
          style={{ marginLeft: '0.4rem' }}
          onClick={a.onClick}
          disabled={a.disabled}
        >
          {a.label}
        </button>
      ))}
    </>
  );
}

const STATUS_LABEL: Record<MembershipListItem['status'], string> = {
  ACTIVE: 'активний',
  SUSPENDED: 'призупинено',
  INVITED: 'запрошено',
};

function MembershipsTable({
  loading, items, actions,
}: {
  loading: boolean;
  items: MembershipListItem[];
  actions: (m: MembershipListItem) => RowAction[];
}) {
  if (loading) return <div>Завантаження…</div>;
  if (items.length === 0) {
    return (
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Список порожній.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
          <th style={{ padding: '0.5rem' }}>Email</th>
          <th style={{ padding: '0.5rem' }}>Ім'я</th>
          <th style={{ padding: '0.5rem' }}>Роль</th>
          <th style={{ padding: '0.5rem' }}>Доступ</th>
          <th style={{ padding: '0.5rem' }}>Статус</th>
          <th style={{ padding: '0.5rem', textAlign: 'right' }}>Дії</th>
        </tr>
      </thead>
      <tbody>
        {items.map((m) => (
          <tr key={m.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
            <td style={{ padding: '0.5rem' }}>{m.email}</td>
            <td style={{ padding: '0.5rem' }}>{m.full_name}</td>
            <td style={{ padding: '0.5rem' }}>{tenantRoleLabel(m.tenant_role)}</td>
            <td style={{ padding: '0.5rem' }}>
              <AccessCell role={m.tenant_role} count={m.org_access_count ?? 0} />
            </td>
            <td style={{ padding: '0.5rem' }}>
              {STATUS_LABEL[m.status] ?? m.status}
              {m.suspended_by_email && (
                <span style={{ marginLeft: '0.4rem', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
                  (від {m.suspended_by_email})
                </span>
              )}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
              <ActionButtons actions={actions(m)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvitationsTable({
  loading, items, actions,
}: {
  loading: boolean;
  items: InvitationListItem[];
  actions: (i: InvitationListItem) => RowAction[];
}) {
  if (loading) return <div>Завантаження…</div>;
  if (items.length === 0) {
    return (
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Немає запрошень в очікуванні.
      </div>
    );
  }
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
          <th style={{ padding: '0.5rem' }}>Email</th>
          <th style={{ padding: '0.5rem' }}>Роль</th>
          <th style={{ padding: '0.5rem' }}>Доступ</th>
          <th style={{ padding: '0.5rem' }}>Запрошено</th>
          <th style={{ padding: '0.5rem', textAlign: 'right' }}>Дії</th>
        </tr>
      </thead>
      <tbody>
        {items.map((inv) => (
          <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-soft)' }}>
            <td style={{ padding: '0.5rem' }}>{inv.email}</td>
            <td style={{ padding: '0.5rem' }}>{tenantRoleLabel(inv.tenant_role)}</td>
            <td style={{ padding: '0.5rem' }}>
              <AccessCell role={inv.tenant_role} count={inv.org_access_count ?? 0} />
            </td>
            <td style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
              {inv.invited_at ? new Date(inv.invited_at).toLocaleDateString('uk-UA') : '—'}
            </td>
            <td style={{ padding: '0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
              <ActionButtons actions={actions(inv)} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
